import React, { useEffect, useState } from "react";
import { GraduationCap, ArrowRight, BookOpen, Layers } from "lucide-react";
import { BASE_URL, courseApi } from "../../../../api/api";
import { Link } from "react-router-dom";
import { token } from "../../../../api/auth";
import { motion } from "framer-motion";

const HomeCourseOverview = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await fetch(`${BASE_URL}${courseApi}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await response.json();
                setCourses(data);
            } catch (error) {
                console.error("Error fetching courses:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCourses();
    }, []);

    const activeCourses = courses.filter(
        (course) => course.status === 1 || course.status === true,
    );

    // Professional fade-up animation variants (No bouncy springs)
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="bg-white py-24 px-4 sm:px-6 lg:px-8 w-full border-t border-gray-100">
            <div className="max-w-7xl mx-auto">
                {/* Structural Editorial Header */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6 border-b border-gray-200 pb-6">
                    <div className="max-w-2xl">
                        <h2 className="text-4xl font-black text-neutral-900 mb-2">
                            Our Popular{" "}
                            <span className="text-yellow-500">Courses.</span>
                        </h2>
                        <p className="text-gray-600 text-base font-medium">
                            Expertly crafted curriculums designed to give you
                            the exact edge needed to conquer your target
                            examinations.
                        </p>
                    </div>

                    {/* View All Button positioned efficiently in the header */}
                    {!loading && activeCourses.length > 6 && (
                        <Link
                            to="/course"
                            className="hidden md:inline-flex items-center px-6 py-2.5 bg-neutral-900 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                        >
                            View All Courses
                            <ArrowRight
                                size={16}
                                className="ml-2 group-hover:translate-x-1 transition-transform"
                            />
                        </Link>
                    )}
                </div>

                {loading ? (
                    /* Clean Skeleton Loader matching the new cards */
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[...Array(6)].map((_, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col animate-pulse"
                            >
                                <div className="w-full aspect-video bg-gray-200"></div>
                                <div className="p-6 flex flex-col flex-grow">
                                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                                    <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
                                    <div className="h-4 bg-gray-100 rounded w-5/6 mb-8"></div>
                                    <div className="mt-auto h-10 bg-gray-200 rounded-lg w-full"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    /* Professional Course Grid */
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {activeCourses.slice(0, 6).map((course) => (
                            <motion.div
                                variants={cardVariants}
                                key={course.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 transition-all duration-300 overflow-hidden flex flex-col group"
                            >
                                <div className="w-full aspect-video overflow-hidden bg-gray-100 relative border-b border-gray-100">
                                    <img
                                        src={
                                            course.image ||
                                            "/assets/placeholder-course.jpg"
                                        }
                                        alt={course.title}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.style.objectFit = "contain";
                                            e.target.style.backgroundColor =
                                                "#f0f0f0";
                                            e.target.style.filter =
                                                "grayscale(100%)";
                                            e.target.style.opacity = "0.5";
                                            e.target.src =
                                                "/assets/logo.png";
                                        }}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                    />
                                    <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/10 transition-colors duration-300"></div>
                                </div>

                                <div className="p-6 flex flex-col flex-grow">
                                    <h3 className="text-xl font-black text-neutral-900 mb-3 group-hover:text-red-700 transition-colors line-clamp-2">
                                        {course.title}
                                    </h3>

                                    <p className="text-gray-600 text-sm font-medium leading-relaxed line-clamp-3 mb-6 flex-grow">
                                        {course.short_description ||
                                            "Comprehensive syllabus coverage with expert mentorship and structured daily targets."}
                                    </p>

                                    <div className="mt-auto pt-4 border-t border-gray-100">
                                        <Link
                                            to={`/course/course-page/${course.id}`}
                                            className="w-full flex items-center justify-between px-4 py-2.5 bg-neutral-900 text-white group-hover:bg-yellow-400 group-hover:text-neutral-900 rounded-lg font-bold text-sm transition-colors duration-300"
                                        >
                                            <div className="flex items-center">
                                                <BookOpen
                                                    size={16}
                                                    className="mr-2 opacity-80"
                                                />
                                                <span>Explore Course</span>
                                            </div>
                                            <ArrowRight
                                                size={16}
                                                className="transform group-hover:translate-x-1 transition-transform"
                                            />
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}

                {/* Mobile View All Button (Hidden on Desktop) */}
                {!loading && activeCourses.length > 6 && (
                    <div className="mt-8 flex justify-center md:hidden">
                        <Link
                            to="/course"
                            className="inline-flex items-center w-full justify-center px-6 py-3 bg-neutral-900 text-white font-bold rounded-lg shadow-sm"
                        >
                            View All Courses
                            <ArrowRight size={16} className="ml-2" />
                        </Link>
                    </div>
                )}

                {!loading && activeCourses.length === 0 && (
                    <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 shadow-sm mt-8 w-full">
                        <GraduationCap
                            size={32}
                            className="mx-auto text-gray-400 mb-3"
                        />
                        <p className="text-neutral-900 font-bold text-base mb-1">
                            No active courses available.
                        </p>
                        <p className="text-gray-500 font-medium text-sm">
                            Please check back later as we update our curriculum.
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
};

export default HomeCourseOverview;
